import Image from 'next/image';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '../../../design-system/components/form/button/Button';
import { Checkbox } from '../../../design-system/components/form/checkbox/Checkbox';
import { Input } from '../../../design-system/components/form/input/Input';
import { FileDownload } from '../../../design-system/components/icons/FileDownload';
import { Typography } from '../../../design-system/components/typography/Typography';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { useLocale } from '../../hooks/useLocale';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { useUser } from '../../providers/user/useUser';
import { isValidEmail } from '../../utilities/email/isValidEmail';
import { PickDomain } from '../../utilities/types/PickDomain';
import { SUBSCRIBE_MODE_NAVIGATION } from '../Newsletter/NewsletterService';
import { useNewsletter } from '../Newsletter/useNewsletter';

import style from './FamilyMiniBookSection.module.css';

const COVER_IMAGE_IDS: Record<string, string> = {
    'fr-FR': '1824699',
    'de-DE': '1825179',
    'es-ES': '1825180',
    'it-IT': '1825181',
};
const FALLBACK_COVER_IMAGE_ID = COVER_IMAGE_IDS['fr-FR'];
const BACKGROUND_IMAGE_ID = '1824698';

type Props = {
    destination: PickDomain<Destination, 'slug'> | null;
    tagSource: string;
};

export default function FamilyMiniBookSection({ destination, tagSource }: Props) {
    const { generatePath } = useUrlGenerator();
    const { send } = useNewsletter();
    const { formatMessage } = useIntl();
    const { user } = useUser();
    const { locale } = useLocale();

    const [email, setEmail] = useState('');
    const [isAccepted, setIsAccepted] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [checkboxError, setCheckboxError] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const coverImageId = COVER_IMAGE_IDS[locale] || FALLBACK_COVER_IMAGE_ID;

    const handleSubmit = () => {
        let hasError = false;

        setEmailError('');
        setCheckboxError('');

        const emailToUse = user ? user.email : email;

        if (!user && (!email || !isValidEmail(email))) {
            setEmailError(formatMessage({ id: 'global.component.familyMinibook.emailError' }));
            hasError = true;
        }

        if (!isAccepted) {
            setCheckboxError(
                formatMessage({ id: 'global.component.familyMinibook.checkboxError' }),
            );
            hasError = true;
        }

        if (!hasError) {
            void send({
                email: emailToUse,
                subscribeMode: SUBSCRIBE_MODE_NAVIGATION,
                subscribeModeDetail: tagSource,
                acquisitionTags: [tagSource],
                destinationSlug: destination?.slug,
                confirmed: true,
                trackingInformation: {
                    subscription: {
                        block: 'family-minibook',
                    },
                },
            });
            setIsSubmitted(true);
        }
    };

    return (
        <>
            <div className={style.familyMiniBookSection}>
                <Image src={BACKGROUND_IMAGE_ID} loader={imageLoaderForId()} fill alt="" />
                <div className={style.contentContainer}>
                    <div className={style.contentWrapper}>
                        <Typography as="div" scale="secondary-m-bold" className={style.title}>
                            {formatMessage(
                                { id: 'global.component.familyMinibook.title' },
                                {
                                    span: (chunks) => (
                                        <span className={style.titleHighlight}>{chunks}</span>
                                    ),
                                },
                            )}
                        </Typography>

                        {isSubmitted ? (
                            <Typography scale="primary-body-bold">
                                {formatMessage({ id: 'global.component.familyMinibook.sendLabel' })}
                            </Typography>
                        ) : (
                            <>
                                <div className={style.cgv}>
                                    <Checkbox
                                        label={formatMessage({
                                            id: 'global.component.familyMinibook.checkbox',
                                        })}
                                        checkboxProps={{
                                            checked: isAccepted,
                                            name: 'confirmation',
                                            required: true,
                                            onChange: (e) => setIsAccepted(e.target.checked),
                                        }}
                                        error={checkboxError}
                                    />
                                </div>
                                <div className={style.form}>
                                    {user === null ? (
                                        <Input
                                            className={style.input}
                                            placeholder={formatMessage({
                                                id: 'global.component.familyMinibook.inputPlaceholder',
                                            })}
                                            error={emailError}
                                            inputProps={{
                                                type: 'email',
                                                value: email,
                                                onChange: (e) => setEmail(e.target.value),
                                            }}
                                        />
                                    ) : null}
                                    <Button
                                        Icon={FileDownload}
                                        type="button"
                                        priority="primary"
                                        onClick={handleSubmit}
                                    >
                                        {formatMessage({
                                            id: 'global.component.familyMinibook.cta',
                                        })}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                    <div className={style.imageCoverWrapper}>
                        <Image
                            className={style.coverImage}
                            loader={imageLoaderForId({
                                aspectRatio: 237 / 335,
                            })}
                            src={coverImageId}
                            width={237}
                            height={335}
                            alt=""
                        />
                    </div>
                </div>
            </div>
            <Typography as="div" scale="primary-xxs-regular" className={style.cgu}>
                {formatMessage(
                    { id: 'global.component.familyMinibook.cgu' },
                    {
                        a: (chunks) => (
                            <a
                                className="link"
                                target="_blank"
                                href={generatePath('cgu')}
                                rel="noreferrer"
                            >
                                {chunks}
                            </a>
                        ),
                    },
                )}
                <a
                    className="link"
                    href={generatePath('confidentiality')}
                    target="_blank"
                    rel="noreferrer"
                >
                    {formatMessage({ id: 'global.component.familyMinibook.confidentiality' })}
                </a>
            </Typography>
        </>
    );
}
